import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { io } from 'socket.io-client';
import { SOCKET_URL } from '../../utils/config';
import API from '../../utils/api';
import {
  FaTachometerAlt, FaComments, FaSignOutAlt, FaBars, FaTimes
} from 'react-icons/fa';

import UserHome from './UserHome';
import UserProfile from './UserProfile';
import UserChat from './UserChat';

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadReplies, setUnreadReplies] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate('/'); };

  // Calculate unread admin replies
  useEffect(() => {
    if (!user) return;

    const myMemberId = String(user.memberId || '');
    const myId = String(user.id || '');

    const fetchUnread = () => {
      API.get('/messages').then(res => {
        const msgs = res.data || [];
        const lastRead = localStorage.getItem(`last_user_read_msg_${user.id}`) || '0';
        const unread = msgs.filter(m => {
          const sRole = m.sender_role || m.senderRole;
          const rId = String(m.recipient_id || m.recipientId || '');
          const isFromAdmin = sRole === 'admin';
          const isForMe = rId === myMemberId || rId === myId;
          return isFromAdmin && isForMe && Number(m.id) > Number(lastRead);
        }).length;

        if (location.pathname === '/user/chat') {
          setUnreadReplies(0);
        } else {
          setUnreadReplies(unread);
        }
      }).catch(() => {});
    };

    fetchUnread();

    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socket.on('connect', () => {
      socket.emit('join_room', {
        userId: myMemberId || myId,
        role: 'user',
        name: user.name || user.username
      });
    });

    socket.on('receive_message', (msg) => {
      const sRole = msg.senderRole || msg.sender_role;
      const rId = String(msg.recipientId || msg.recipient_id || '');
      const isFromAdmin = sRole === 'admin';
      const isForMe = rId === myMemberId || rId === myId;

      if (isFromAdmin && isForMe) {
        if (window.location.pathname === '/user/chat') {
          localStorage.setItem(`last_user_read_msg_${user.id}`, String(msg.id));
          setUnreadReplies(0);
        } else {
          setUnreadReplies(prev => prev + 1);
        }
      }
    });

    return () => socket.disconnect();
  }, [user]);

  // When user is on /user/chat, clear unread count
  useEffect(() => {
    if (location.pathname === '/user/chat' && user) {
      API.get('/messages').then(res => {
        const msgs = res.data || [];
        if (msgs.length > 0) {
          const maxId = Math.max(...msgs.map(m => Number(m.id) || 0));
          localStorage.setItem(`last_user_read_msg_${user.id}`, String(maxId));
        }
        setUnreadReplies(0);
      }).catch(() => {});
    }
  }, [location.pathname, user]);

  const navItems = [
    { path: '/user/dashboard', label: 'Dashboard', icon: <FaTachometerAlt size={15} /> },
    { path: '/user/chat', label: 'Chat Support', icon: <FaComments size={15} />, badge: unreadReplies },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="dashboard-layout">
      {/* Mobile Header Bar */}
      <div className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle Navigation Menu"
          >
            {mobileMenuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
          </button>
          <img src="/logo.png" alt="Sri Karunasai Logo" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'contain' }} />
          <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>Member Portal</span>
        </div>
        <button
          className="mobile-logout-btn"
          onClick={handleLogout}
          title="Logout"
        >
          <FaSignOutAlt size={14} /> Logout
        </button>
      </div>

      {/* Mobile Drawer Backdrop */}
      {mobileMenuOpen && (
        <div
          className="mobile-drawer-backdrop"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-logo">
          <img src="/logo.png" alt="Sri Karunasai Logo" className="sidebar-logo-img" />
          <div className="sidebar-logo-text">
            <h3>Sri Karunasai</h3>
            <span>Member Portal</span>
          </div>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar" style={{ background: 'linear-gradient(135deg, #15803d, #22c55e)' }}>
            {(user?.name || user?.username || 'M').charAt(0).toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.name || user?.username}
            </div>
            <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 500, fontFamily: 'monospace' }}>
              {user?.memberId}
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section-title">My Account</div>
          {navItems.map(item => (
            <div
              key={item.path}
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => {
                navigate(item.path);
                setMobileMenuOpen(false);
              }}
            >
              {item.icon}
              <span>{item.label}</span>
              {item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="nav-item" onClick={handleLogout} style={{ color: '#ef4444' }}>
            <FaSignOutAlt size={14} /> Logout
          </div>
        </div>
      </aside>

      <main className="main-content">
        <Routes>
          <Route path="dashboard" element={<UserHome />} />
          <Route path="profile" element={<UserProfile />} />
          <Route path="chat" element={<UserChat />} />
          <Route path="*" element={<UserHome />} />
        </Routes>
      </main>
    </div>
  );
}
